import { useEffect, useRef, type MutableRefObject, type ReactNode } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import { createTiptapModules } from '../../tiptap/bundle';
import { DOC_TYPES, docTypeDef } from '../../lib/docs';
import { apiFetchRaw } from '../../api/client';
import type { DocType } from '../../lib/types';

/**
 * The page cell in edit mode: the type bar, and whichever editor that type needs.
 *
 * THE LAST UNCONTROLLED FORM IN THE APP, and the reason this component exists rather than a
 * thin wrapper. The vanilla editor kept its state in the DOM: the title in an `<input>` read
 * back by `document.getElementById('doc-title-input').value` on save, the URL in a module-level
 * `docEditorExternalInput` handle pointing at a live element, the content inside a Tiptap
 * instance held in a global. Save re-read all three from wherever they happened to be. The
 * original research flagged that pattern as "the most likely source of silent data-loss
 * regressions" — a save that reads an element that has since been re-rendered writes whatever
 * is there now, and nothing errors.
 *
 * Here the title, the URL and the chosen type are React state owned by the overlay, and the
 * content comes from the editor instance's own `getJSON()`. Nothing is queried out of the DOM.
 *
 * WHY THE TOOLBAR LIVES INSIDE THIS COMPONENT and not beside it: its buttons light up from
 * `editor.isActive(...)`, which is only correct if something re-renders on every transaction.
 * `useEditor` does exactly that, so the toolbar has to be under the hook that owns the editor.
 * The vanilla version achieved the same thing by rebuilding the whole bar from
 * `selectionUpdate` and `transaction` handlers — which is this, spelled imperatively.
 *
 * DOM ORDER IS PRESERVED — toolbar, type bar, content — because `.docframe-wrap` is a flex
 * column and the content cell is the one that takes the remaining height.
 */

/**
 * ONE extension set for the whole app, from the vendored bundle.
 *
 * It must stay identical to `server/src/docs/tiptapExtensions.ts`: content is stored as Tiptap
 * JSON and rendered server-side with that list, and a mismatch does not error — it silently
 * drops nodes on one side of the round trip.
 */
const TIPTAP = createTiptapModules();

export interface LoadedDoc {
  id: string;
  title: string;
  docType?: DocType;
  sourceUrl?: string | null;
  contentJson?: unknown;
  /**
   * The locale the server RESOLVED — not necessarily the one asked for. `GET /api/docs/:id`
   * falls back to the default when the requested language has no translation, and says which
   * one it served. `WorkflowControl` keys on this; the editor itself does not read it.
   */
  locale?: string;
  /** Workflow standing of that resolved translation (V0.38). See `WorkflowControl`. */
  workflowState?: string;
  reviewedAt?: string | null;
  reviewedByName?: string | null;
}

/** The editor's type selector: what this page HOLDS, chosen by an admin and never inferred. */
function TypeBar({ value, onChange }: { value: DocType; onChange: (t: DocType) => void }) {
  return (
    <div id="docedit-typebar" className="docedit-typebar">
      <label htmlFor="docedit-type">This page is</label>
      <select
        className="fld"
        id="docedit-type"
        style={{ width: 'auto', minWidth: 170, padding: '7px 10px' }}
        value={value}
        onChange={(e) => onChange(e.target.value as DocType)}
      >
        {DOC_TYPES.map((t) => (
          <option key={t.key} value={t.key}>
            {t.label}
          </option>
        ))}
      </select>
      <span className="typehint">{docTypeDef(value).hint}</span>
    </div>
  );
}

/**
 * An embedded or linked page has no content, only a target.
 *
 * It shares the overlay, the title field, the navigation band and the Save button with the
 * written editor, because from the reader's side all three are "the documentation for this
 * node" and only the middle of the page differs.
 */
function UrlEditor({
  docType,
  url,
  onUrlChange,
}: {
  docType: DocType;
  url: string;
  onUrlChange: (u: string) => void;
}) {
  return (
    <div id="docedit-content" className="docedit-content">
      <div className="doclink-edit">
        <label htmlFor="docedit-url">{docType === 'iframe' ? 'Embedded URL' : 'Linked URL'}</label>
        <input
          id="docedit-url"
          type="url"
          value={url}
          spellCheck={false}
          placeholder="https://…"
          onChange={(e) => onUrlChange(e.target.value)}
        />
        <p>
          {docType === 'iframe'
            ? 'Shown inside InfoStation, with the document navigation around it and a button to open it in a new tab. The site has to allow being embedded.'
            : 'Opens in a new browser tab. The page here is just the button that takes you there.'}
        </p>
      </div>
    </div>
  );
}

/** Upload an image to this page and insert it where the caret is. */
async function uploadImage(docId: string, file: File, editor: Editor): Promise<void> {
  const fd = new FormData();
  fd.append('image', file);
  // Not through `apiRequest`: this body is multipart, and setting a JSON content type would
  // strip the boundary the server needs to parse it. `apiFetchRaw` is the same `fetch` plus
  // the 401 report, so an expired session says so here as it does everywhere else.
  const r = await apiFetchRaw(`/api/docs/${docId}/images`, {
    method: 'POST',
    body: fd,
  });
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || 'Upload failed');
  }
  const { url } = (await r.json()) as { url: string };
  editor.chain().focus().setImage({ src: url }).run();
}

function Toolbar({ editor, docId }: { editor: Editor; docId: string }) {
  const file = useRef<HTMLInputElement>(null);

  // Label, what it does, whether it is currently on. Same set and same wording as the
  // vanilla bar — this is a port, not a redesign of the editing surface.
  const buttons: [string, () => void, boolean][] = [
    ['Bold', () => editor.chain().focus().toggleBold().run(), editor.isActive('bold')],
    ['Italic', () => editor.chain().focus().toggleItalic().run(), editor.isActive('italic')],
    ['H2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive('heading', { level: 2 })],
    ['H3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive('heading', { level: 3 })],
    ['Bullet list', () => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList')],
    ['Numbered list', () => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList')],
    ['Code', () => editor.chain().focus().toggleCode().run(), editor.isActive('code')],
    [
      'Table',
      () => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run(),
      false,
    ],
    [
      'Link',
      () => {
        const url = prompt('Link URL:');
        if (url) editor.chain().focus().setLink({ href: url }).run();
      },
      editor.isActive('link'),
    ],
    ['Image', () => file.current?.click(), false],
  ];

  return (
    <div id="docedit-toolbar" className="docedit-toolbar">
      {buttons.map(([label, action, on]) => (
        <button
          key={label}
          type="button"
          className={on ? 'on' : undefined}
          onClick={(e) => {
            e.preventDefault();
            action();
          }}
        >
          {label}
        </button>
      ))}
      {/* The vanilla version appended ONE hidden input to <body> and reused it for the life
          of the tab, rebinding its onchange per click. This one belongs to the toolbar and
          goes when the toolbar does. */}
      <input
        ref={file}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const chosen = e.target.files?.[0];
          e.target.value = '';
          if (!chosen) return;
          uploadImage(docId, chosen, editor).catch((err: Error) =>
            alert(err.message || 'Could not upload the image.'),
          );
        }}
      />
    </div>
  );
}

function StandardEditor({
  docId,
  contentJson,
  typeBar,
  editorRef,
}: {
  docId: string;
  contentJson: unknown;
  typeBar: ReactNode;
  editorRef: MutableRefObject<Editor | null>;
}) {
  const editor = useEditor({
    extensions: TIPTAP.extensions,
    content: contentJson as never,
    autofocus: false,
  });

  // Published rather than lifted into state: the save reads it, nothing renders from it, and
  // an editor instance in React state would re-render the tree on every keystroke.
  useEffect(() => {
    editorRef.current = editor;
    return () => {
      editorRef.current = null;
    };
  }, [editor, editorRef]);

  if (!editor) return <>{typeBar}</>;
  return (
    <>
      <Toolbar editor={editor} docId={docId} />
      {typeBar}
      <EditorContent id="docedit-content" className="docedit-content" editor={editor} />
    </>
  );
}

export interface DocEditorPaneProps {
  docId: string;
  doc: LoadedDoc | null;
  /** Null until the GET lands; a string when it failed and this is what to say. */
  message: string | null;
  docType: DocType;
  onDocTypeChange: (t: DocType) => void;
  url: string;
  onUrlChange: (u: string) => void;
  editorRef: MutableRefObject<Editor | null>;
}

export function DocEditorPane({
  docId,
  doc,
  message,
  docType,
  onDocTypeChange,
  url,
  onUrlChange,
  editorRef,
}: DocEditorPaneProps) {
  const typeBar = <TypeBar value={docType} onChange={onDocTypeChange} />;

  if (message || !doc) {
    return (
      <div id="docedit-content" className="docedit-content">
        <div style={{ padding: 40, color: 'var(--ink-faint)' }}>{message ?? 'Loading editor…'}</div>
      </div>
    );
  }

  if (docType !== 'standard') {
    return (
      <>
        {typeBar}
        <UrlEditor docType={docType} url={url} onUrlChange={onUrlChange} />
      </>
    );
  }

  return (
    // Keyed on the page: opening a different document must build a new editor rather than
    // re-using one holding the previous page's document, which is what `destroy()` + `new
    // Editor()` did imperatively.
    <StandardEditor
      key={docId}
      docId={docId}
      contentJson={doc.contentJson}
      typeBar={typeBar}
      editorRef={editorRef}
    />
  );
}
